import { useMemo, useState } from 'react';
import { collection, orderBy, query } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useDocs } from '@/lib/useDocs';
import type { Competency, TaxonomyTag } from '@/types/domain';
import { strings } from '@/strings';

const DEFAULT_LEVEL = 3;

/**
 * Only leaves are selectable. Tagging someone with the domain "التسويق الرقمي"
 * says nothing the matching engine can use — the whole reason the taxonomy has
 * two levels is that media buying and content strategy are different jobs.
 *
 * A tag already chosen is removed from the add list, so the duplicate the
 * server refuses cannot be constructed in the first place.
 */
export function CompetencyPicker({
  value, onChange,
}: { value: Competency[]; onChange: (next: Competency[]) => void }) {
  const { rows } = useDocs<TaxonomyTag>(query(collection(db, 'taxonomy'), orderBy('path')), []);
  const [pending, setPending] = useState('');

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  const chosen = new Set(value.map((c) => c.tagId));
  const available = rows.filter((r) => r.parentId !== null && r.active !== false && !chosen.has(r.id));

  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-medium text-ink">{strings.competenciesTitle}</span>

      <ul className="flex flex-col gap-2">
        {value.map((entry, index) => (
          <li key={entry.tagId} className="flex items-center gap-3 rounded-md border border-border-subtle p-2">
            <span className="flex-1 text-sm">{byId.get(entry.tagId)?.nameAr || entry.tagId}</span>
            <label className="flex items-center gap-1 text-xs text-muted" htmlFor={`level-${entry.tagId}`}>
              {strings.competencyLevel}
              <select
                id={`level-${entry.tagId}`}
                value={entry.level}
                onChange={(e) => {
                  const next = [...value];
                  next[index] = { ...entry, level: Number(e.target.value) };
                  onChange(next);
                }}
                className="rounded border border-border-subtle px-2 py-1 text-ink"
              >
                {[1, 2, 3, 4, 5].map((level) => <option key={level} value={level}>{level}</option>)}
              </select>
            </label>
            <button
              type="button"
              onClick={() => onChange(value.filter((c) => c.tagId !== entry.tagId))}
              className="text-xs text-danger"
            >
              {strings.competencyRemove}
            </button>
          </li>
        ))}
      </ul>

      <div className="flex items-end gap-2">
        <label className="flex flex-1 flex-col gap-1 text-xs text-muted" htmlFor="add-competency">
          {strings.competencyAdd}
          <select
            id="add-competency"
            value={pending}
            onChange={(e) => setPending(e.target.value)}
            className="rounded-md border border-border-subtle px-2 py-1 text-sm text-ink"
          >
            <option value="" />
            {available.map((tag) => <option key={tag.id} value={tag.id}>{tag.nameAr}</option>)}
          </select>
        </label>
        <button
          type="button"
          disabled={!pending}
          onClick={() => { onChange([...value, { tagId: pending, level: DEFAULT_LEVEL }]); setPending(''); }}
          className="rounded-md border border-border-subtle px-3 py-1 text-sm disabled:opacity-50"
        >
          {strings.competencyAdd}
        </button>
      </div>
    </div>
  );
}
